import { BBox } from "geojson";

import { DEFAULT_INFERENCE_FORM } from "@/features/forge/constants";
import { getImageObjectSafe } from "@/utils/images";

import { roundBBoxTo } from "../../../../features/asset/canvas/artboard/geometry";
import {
  GuidanceFileParameters,
  InferenceParameters,
  SessionStateInferenceParameters,
} from "../../../../types";
import { referenceMetadata } from "../../../ui-v2/References/constants";
import { ReferenceTypeEnum } from "../../../ui-v2/References/types";
import { formatReferenceImageWithTypeForAPI } from "../../../ui-v2/References/utils";
import { DEFAULT_DENSITY } from "../../constants";
import { formatFormWithStyleValuesForAPI } from "../../fields/Style/utils";
import {
  getSimilarityFromWeight,
  getWeightFromSimilarity,
} from "../../utils/utils";
import {
  formatFormValuesForCommonAPI as formatCreateFormValuesForCommonAPI,
  formatInferenceParamsForForm as formatInferenceParamsForCreateForm,
} from "../2D/utils";
import { formatInferenceParamsForForm as formatInferenceParamsForPromptEditForm } from "../PromptEditImage/utils";
import {
  DEFAULT_CANVAS_CREATE_FORM_VALUES,
  DEFAULT_CANVAS_PROMPT_EDIT_FORM_VALUES,
  FORM_FIELDS_CANVAS_CREATE,
  FORM_FIELDS_CANVAS_PROMPT_EDIT,
} from "./constants";
import {
  CanvasSelectionType,
  FormTypeCanvasCreate2D,
  FormTypeCanvasPromptEdit,
} from "./types";

export const getDefaultCanvasCreateFormValues = () =>
  structuredClone(DEFAULT_CANVAS_CREATE_FORM_VALUES);

export const getDefaultCanvasPromptEditFormValues = () =>
  structuredClone(DEFAULT_CANVAS_PROMPT_EDIT_FORM_VALUES);

export const formatInferenceParamsForCanvasCreateForm = async (
  params: SessionStateInferenceParameters,
): Promise<Partial<FormTypeCanvasCreate2D>> => {
  const form2DParams = await formatInferenceParamsForCreateForm(params);
  return {
    ...form2DParams,
    [FORM_FIELDS_CANVAS_CREATE.CANVAS_SELECTION]:
      await getCanvasSelectionFromFiles(params.files, DEFAULT_DENSITY),
  };
};

const formatCanvasSelectionForAPI = (
  inputForm: FormTypeCanvasCreate2D | FormTypeCanvasPromptEdit,
): InferenceParameters => {
  const safeRoundBBoxTo = (bbox: BBox, roundToNumber: number) => {
    return bbox ? roundBBoxTo(bbox, roundToNumber) : undefined;
  };
  const isRefill = inputForm[FORM_FIELDS_CANVAS_CREATE.REFILL] === true;
  return {
    files: inputForm[FORM_FIELDS_CANVAS_CREATE.CANVAS_SELECTION].image?.src
      ? [
          {
            url: inputForm[FORM_FIELDS_CANVAS_CREATE.CANVAS_SELECTION].image
              ?.src,
            type: inputForm[FORM_FIELDS_CANVAS_CREATE.CANVAS_SELECTION].type,
            preprocess:
              referenceMetadata(
                inputForm[FORM_FIELDS_CANVAS_CREATE.CANVAS_SELECTION].type,
              )?.preprocess ?? false,
            weight: isRefill
              ? 1
              : getWeightFromSimilarity(
                  inputForm[FORM_FIELDS_CANVAS_CREATE.CANVAS_SELECTION]
                    .similarity,
                ),
          },
        ]
      : [],
    maskImageUrl:
      inputForm[FORM_FIELDS_CANVAS_CREATE.CANVAS_SELECTION].image?.maskImageUrl,
    maskForTransparency:
      inputForm[FORM_FIELDS_CANVAS_CREATE.CANVAS_SELECTION].maskForTransparency,
    maskEdgeRadius:
      inputForm[FORM_FIELDS_CANVAS_CREATE.CANVAS_SELECTION].maskEdgeRadius,
    maskBlendRadius:
      inputForm[FORM_FIELDS_CANVAS_CREATE.CANVAS_SELECTION].maskBlendRadius,
    canvasFrameBBox: safeRoundBBoxTo(
      inputForm[FORM_FIELDS_CANVAS_CREATE.CANVAS_SELECTION].canvasFrameBBox,
      1,
    ),
    canvasGenerationBBox: safeRoundBBoxTo(
      inputForm[FORM_FIELDS_CANVAS_CREATE.CANVAS_SELECTION]
        .canvasGenerationBBox,
      1,
    ),
    returnBBox:
      inputForm[FORM_FIELDS_CANVAS_CREATE.CANVAS_SELECTION].returnBBox,
    returnMaskedAreaOnly: true,
  };
};

const formatCanvasCreateFormValuesForAPI = (
  inputForm: FormTypeCanvasCreate2D,
): InferenceParameters => {
  const parameters = {
    ...formatCreateFormValuesForCommonAPI(inputForm),
    refill: inputForm[FORM_FIELDS_CANVAS_CREATE.REFILL] || false,
    guidanceScale: inputForm[FORM_FIELDS_CANVAS_CREATE.GUIDANCE_SCALE],
    ...formatCanvasSelectionForAPI(inputForm),
  };
  return parameters;
};

export const formatCanvasCreateFormValuesForInferenceAPI = (
  inputForm: FormTypeCanvasCreate2D,
): InferenceParameters => ({
  ...formatCanvasCreateFormValuesForAPI(inputForm),
});

export const formatCanvasCreateFormValuesForPricingApi = (
  inputForm: FormTypeCanvasCreate2D,
): InferenceParameters[] => {
  const inferenceParams =
    formatCanvasCreateFormValuesForInferenceAPI(inputForm);

  return [
    {
      ...inferenceParams,
      files: inferenceParams.files
        ? inferenceParams.files.filter((file) => !file.url.startsWith("data:"))
        : [],
      maskImageUrl: inferenceParams.maskImageUrl?.startsWith("data:")
        ? null
        : inferenceParams.maskImageUrl,
    },
  ];
};

export const formatCanvasCreateFormValuesForSessionAPI = (
  inputForm: FormTypeCanvasCreate2D,
): SessionStateInferenceParameters => ({
  ...formatCanvasCreateFormValuesForAPI(inputForm),
});

export const formatInferenceParamsForCanvasPromptEditForm = async (
  params: SessionStateInferenceParameters,
): Promise<Partial<FormTypeCanvasPromptEdit>> => {
  const outputForm = await formatInferenceParamsForPromptEditForm(params);
  if (params) {
    if (params.width && params.height) {
      outputForm[FORM_FIELDS_CANVAS_PROMPT_EDIT.DIMENSIONS] = [
        params.width,
        params.height,
      ];
      outputForm[FORM_FIELDS_CANVAS_PROMPT_EDIT.CANVAS_SELECTION] =
        await getCanvasSelectionFromFiles(params.files, DEFAULT_DENSITY);
    }
  }
  return outputForm;
};

const formatCanvasPromptEditFormValuesForAPI = (
  inputForm: FormTypeCanvasPromptEdit,
): InferenceParameters => {
  const parameters: InferenceParameters = {
    ...DEFAULT_INFERENCE_FORM.parameters,
    ...formatFormWithStyleValuesForAPI(inputForm),
    prompt: inputForm[FORM_FIELDS_CANVAS_PROMPT_EDIT.PROMPT_TEXT],
    promptLanguage: inputForm[FORM_FIELDS_CANVAS_PROMPT_EDIT.PROMPT_LANGUAGE],
    promptEnhancementMode:
      inputForm[FORM_FIELDS_CANVAS_PROMPT_EDIT.PROMPT_ENHANCEMENT_MODE],
    width: inputForm[FORM_FIELDS_CANVAS_PROMPT_EDIT.DIMENSIONS][0],
    height: inputForm[FORM_FIELDS_CANVAS_PROMPT_EDIT.DIMENSIONS][1],
    batchSize: 1,
    ...formatCanvasSelectionForAPI(inputForm),
  };

  // Reference images from the input form will be used as context
  inputForm[FORM_FIELDS_CANVAS_PROMPT_EDIT.REFERENCES].forEach((image) => {
    parameters.files.push(
      formatReferenceImageWithTypeForAPI(image, "REFERENCE_IMAGE"),
    );
  });

  return parameters;
};

export const formatCanvasPromptEditFormValuesForInferenceAPI = (
  inputForm: FormTypeCanvasPromptEdit,
): InferenceParameters => ({
  ...formatCanvasPromptEditFormValuesForAPI(inputForm),
});

export const formatCanvasPromptEditFormValuesForSessionAPI = (
  inputForm: FormTypeCanvasPromptEdit,
): SessionStateInferenceParameters => ({
  ...formatCanvasPromptEditFormValuesForAPI(inputForm),
});

export const formatCanvasPromptEditFormValuesForPricingApi = (
  inputForm: FormTypeCanvasPromptEdit,
): InferenceParameters[] => [formatCanvasPromptEditFormValuesForAPI(inputForm)];

export const getCanvasSelectionFromFiles = async (
  files: GuidanceFileParameters[] | undefined,
  density: number,
): Promise<CanvasSelectionType> => {
  const canvasSelectionFile = files?.find((file) => file.selection);

  if (canvasSelectionFile) {
    const imageObject = await getImageObjectSafe(canvasSelectionFile.url);

    if (imageObject) {
      return {
        type: canvasSelectionFile.type as ReferenceTypeEnum,
        image: {
          src: canvasSelectionFile.url,
          width: imageObject.width,
          height: imageObject.height,
        },
        similarity: getSimilarityFromWeight(canvasSelectionFile.weight),
        density,
      };
    }
  }

  return DEFAULT_CANVAS_CREATE_FORM_VALUES[
    FORM_FIELDS_CANVAS_CREATE.CANVAS_SELECTION
  ];
};
